/**
 * Cart Drawer Component
 * Slides down from the header, anchored near the cart icon.
 * Shows cart contents when items exist, or empty state message.
 */

import { useEffect, useRef, useCallback } from "react";
import { useCart } from "@/hooks/useCart";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { StockIndicator } from "@/components/common/StockIndicator/StockIndicator";
import { isLineDiscounted } from "@/utils/discount";
import { isFreeLine } from "@/types/cart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { localizeUrl } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { maskImeiCompact } from "@/utils/imei";
import { formatVariantAttributes } from "@/utils/variant";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./CartDrawer.module.css";

export function CartDrawer() {
  const {
    cart,
    isLoading,
    isDrawerOpen,
    setDrawerOpen,
    cartItemCount,
    cartUrl,
  } = useCart();
  const localizedTexts = useLocalizedTexts();

  const drawerRef = useRef<HTMLDivElement>(null);

  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveredRef = useRef(false);

  const clearAutoDismiss = useCallback(() => {
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }
  }, []);

  const startAutoDismiss = useCallback(() => {
    clearAutoDismiss();
    autoDismissRef.current = setTimeout(() => {
      if (!isHoveredRef.current) {
        setDrawerOpen(false);
      }
    }, 4000);
  }, [clearAutoDismiss, setDrawerOpen]);

  // Auto-dismiss: start timer when drawer opens, clear on close
  useEffect(() => {
    if (isDrawerOpen) {
      startAutoDismiss();
    } else {
      clearAutoDismiss();
    }
    return clearAutoDismiss;
  }, [isDrawerOpen, startAutoDismiss, clearAutoDismiss]);

  const handleMouseEnter = useCallback(() => {
    isHoveredRef.current = true;
    clearAutoDismiss();
  }, [clearAutoDismiss]);

  const handleMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
    if (isDrawerOpen) {
      startAutoDismiss();
    }
  }, [isDrawerOpen, startAutoDismiss]);

  // Close drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isDrawerOpen) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDrawerOpen, setDrawerOpen]);

  // Close drawer on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target as Node)
      ) {
        // Don't close if the click is on the cart toggle button
        const target = e.target as HTMLElement;
        if (target.closest("[data-cart-toggle]")) return;
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDrawerOpen, setDrawerOpen]);

  const hasItems = cart && cart.lines.length > 0;

  return (
    <div
      ref={drawerRef}
      className={`${styles.drawer} ${isDrawerOpen ? styles.drawerOpen : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="dialog"
      aria-label={t(
        localizedTexts,
        "Tracker.Cart.ShoppingCart",
        "Shopping cart"
      )}
      aria-hidden={!isDrawerOpen}
    >
      <div className={styles.drawerContent}>
        {/* Header */}
        <div className={styles.drawerHeader}>
          <h2
            className={styles.drawerTitle}
          >{`${t(localizedTexts, "Tracker.Cart.YourCart", "Your Cart")} (${cartItemCount})`}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setDrawerOpen(false)}
            aria-label={t(
              localizedTexts,
              "Tracker.Cart.CloseCart",
              "Close cart"
            )}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.drawerBody}>
          {isLoading && (
            <p className={styles.emptyText}>
              {t(localizedTexts, "Tracker.Cart.Loading", "Loading cart...")}
            </p>
          )}

          {!isLoading && !hasItems && (
            <p className={styles.emptyText}>
              {t(localizedTexts, "Tracker.Cart.NoItems", "No items in cart")}
            </p>
          )}

          {!isLoading && hasItems && (
            <ul className={styles.lineList}>
              {cart.lines.map((line) => (
                <li key={line.id} className={styles.lineItem}>
                  <div className={styles.lineImage}>
                    {line.image ? (
                      <img
                        {...fixedCmsImage(line.image.url, { width: 56 })}
                        alt={line.image.altText || line.name}
                        className={styles.lineImg}
                      />
                    ) : (
                      <div className={styles.linePlaceholder} />
                    )}
                  </div>
                  <div className={styles.lineDetails}>
                    <span className={styles.lineName}>{line.name}</span>
                    {formatVariantAttributes(line.variantAttributes) && (
                      <span className={styles.lineVariant}>
                        {formatVariantAttributes(line.variantAttributes)}
                      </span>
                    )}
                    {/* The version was never chosen. The picker lives on the cart
                        page, not here — the drawer is too narrow for one per line
                        — but saying nothing would make the drawer look complete
                        when checkout is in fact blocked. */}
                    {line.requiresVariantSelection === true && (
                      <span className={styles.lineNeedsVariant}>
                        {t(
                          localizedTexts,
                          "Tracker.Cart.ChooseVariantRequired",
                          "Choose a size before continuing"
                        )}
                      </span>
                    )}
                    {line.bundleId && (
                      <span className={styles.lineVariant}>
                        {line.bundleName
                          ? t(
                              localizedTexts,
                              "Tracker.Cart.PartOfBundle",
                              "Part of {bundle}"
                            ).replace("{bundle}", line.bundleName)
                          : t(
                              localizedTexts,
                              "Tracker.Generic.Bundle",
                              "Bundle"
                            )}
                      </span>
                    )}
                    {line.deviceImei && (
                      <span className={styles.lineDevice}>
                        {t(
                          localizedTexts,
                          "Tracker.Cart.ForDevice",
                          "For device"
                        )}
                        : {line.deviceName ?? ""}
                        {" · "}
                        {t(localizedTexts, "Tracker.Cart.Imei", "IMEI")}{" "}
                        {maskImeiCompact(line.deviceImei)}
                      </span>
                    )}
                    <span className={styles.lineQtyPrice}>
                      {isLineDiscounted(line) ? (
                        <>
                          {line.quantity} x{" "}
                          <DiscountedUnitPrice
                            variant="discount"
                            standardInclVat={
                              line.formattedUnitPriceWithoutAdjustments
                            }
                            accentPrice={line.formattedUnitPrice}
                            size="sm"
                          />
                        </>
                      ) : (
                        `${line.quantity} x ${line.formattedUnitPrice}`
                      )}
                    </span>
                    <StockIndicator level={line.stockLevel} surface="cart" />
                  </div>
                  <span className={styles.lineTotal}>
                    {/* A campaign's free item must not read as "0,00 €". */}
                    {isFreeLine(line)
                      ? t(localizedTexts, "Tracker.Cart.Free", "Free")
                      : line.formattedTotalPrice}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer - only if cart has items */}
        {!isLoading && hasItems && (
          <div className={styles.drawerFooter}>
            {cart.subtotalPrice && (
              <div className={styles.subtotal}>
                <span>
                  {t(localizedTexts, "Tracker.Checkout.Subtotal", "Subtotal")}
                </span>
                <span className={styles.subtotalPrice}>
                  {cart.subtotalPrice}
                </span>
              </div>
            )}
            <a
              href={cartUrl ?? localizeUrl("/cart", getLocale().language)}
              className={styles.proceedButton}
            >
              {t(
                localizedTexts,
                "Tracker.Cart.ProceedToCart",
                "Proceed to cart"
              )}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartDrawer;
